<template>
  <div
    :class="['server-side-data-table', { 'server-side': useServerSide }]"
    v-if="show && visibility"
  >
    <!-- Header Section -->
    <div class="title_table">
      <div class="title_settings">
        <div class="table_title">
          {{ table_title }}
        </div>

        <div class="action_buttons_div">
          <!-- Left side action buttons slot -->
          <div class="left-actions">
            <slot name="left-actions">
              <!-- Default: Filter toggle button -->
              <button
                v-if="show_filter_toggle"
                class="action-btn filter-btn"
                @click="$emit('toggle-filter')"
                :class="{ active: filterVisible }"
              >
                <i class="fa-solid fa-filter"></i>
                Filtros
              </button>
            </slot>
            <!-- Select All / Deselect All button -->
            <button
              v-if="selectable"
              class="action-btn"
              :class="{ active: allSelected }"
              @click="toggleSelectAll"
              :title="allSelected ? 'Desselecionar tudo' : 'Selecionar tudo'"
            >
              <i :class="allSelected ? 'fa-regular fa-square' : 'fa-solid fa-check-double'"></i>
              {{ allSelected ? 'Desselecionar' : 'Selecionar tudo' }}
            </button>
          </div>

          <!-- Right side action buttons slot -->
          <div class="right-actions">
            <slot name="right-actions">
              <!-- Default: Export and Close buttons -->
              <button
                v-if="show_groupby_btn"
                @click="$emit('toggle-groupby')"
                class="action-btn"
                :class="{ active: groupbyActive }"
                title="Agrupar colunas"
              >
                <i :class="groupbyActive ? 'fa-solid fa-grip-vertical' : 'fa-solid fa-grip-lines'"></i>
              </button>
              <div v-if="isTransferMode" class="transfer-search-container"
              >
                <!-- Cliente Multiselect (Step 1) -->
                <multiselect
                  v-model="transferTargetCliente"
                  :options="clientesFiltered"
                  :multiple="false"
                  :searchable="true"
                  :loading="clientesLoading"
                  :internal-search="false"
                  :clear-on-select="true"
                  :close-on-select="true"
                  :options-limit="50"
                  :limit="50"
                  :max-height="300"
                  :show-no-results="true"
                  placeholder="1. Selecione o cliente..."
                  label="cliente"
                  track-by="id"
                  @search-change="buscarClientes"
                  @input="onClienteSelected"
                  class="transfer-multiselect"
                >
                  <template slot="noResult">
                    <span>Nenhum cliente encontrado. Digite para buscar...</span>
                  </template>

                  <template slot="noOptions">
                    <span>Digite pelo menos 3 caracteres para buscar...</span>
                  </template>

                  <template slot="singleLabel" slot-scope="props">
                    <span v-if="props.option" class="option__desc">
                      <span class="option__title">{{ props.option.cliente }}</span>
                    </span>
                  </template>
                </multiselect>

                <!-- SubCliente Multiselect (Step 2) - Only enabled after Cliente is selected -->
                <multiselect
                  v-model="transferTargetSubcliente"
                  :options="subclientesFiltered"
                  :multiple="false"
                  :searchable="true"
                  :loading="subclientesLoading"
                  :internal-search="false"
                  :clear-on-select="true"
                  :close-on-select="true"
                  :options-limit="50"
                  :limit="50"
                  :max-height="300"
                  :show-no-results="true"
                  :disabled="!transferTargetCliente"
                  :placeholder="transferTargetCliente ? '2. Selecione o sub-cliente...' : 'Primeiro selecione um cliente'"
                  label="nome"
                  track-by="id"
                  @search-change="buscarSubClientes"
                  class="transfer-multiselect"
                  :class="{ 'disabled': !transferTargetCliente }"
                >
                  <template slot="noResult">
                    <span>Nenhum sub-cliente encontrado. Digite para buscar...</span>
                  </template>

                  <template slot="noOptions">
                    <span>Digite pelo menos 3 caracteres para buscar...</span>
                  </template>

                  <template slot="singleLabel" slot-scope="props">
                    <span v-if="props.option" class="option__desc">
                      <span class="option__title">{{ props.option.nome }}</span>
                    </span>
                  </template>
                </multiselect>

                <button
                  v-if="internalSelectedRows.length > 0 && transferTargetSubcliente != null"
                  class="action-btn export-btn"
                  @click="confirmTransfer"
                >
                  Confirmar
                  <i  class="fa-solid fa-check"></i>
                </button>
              </div>
             
              <button
                v-if="show_export"
                class="action-btn export-btn"
                @click="toggleExportDropdown"
                :disabled="isExporting"
              >
                <template v-if="isExporting">
                  <i class="fa-solid fa-spinner fa-spin"></i>
                  A exportar...
                </template>
                <template v-else>
                  Exportar
                  <i v-if="!isExportOpen" class="fa-solid fa-caret-down"></i>
                  <i v-else class="fa-solid fa-caret-up"></i>
                </template>
              </button>
            

              <!-- Export dropdown menu -->
              <div v-if="isExportOpen" class="export-dropdown">
                <ul>
                  <li @click="exportData('pdf')">
                    PDF <i class="fa-solid fa-file-pdf"></i>
                  </li>
                  <li @click="exportData('csv')">
                    CSV <i class="fa-solid fa-file-csv"></i>
                  </li>
                  <li @click="exportData('xlsx')">
                    XLSX <i class="fa-solid fa-file-excel"></i>
                  </li>
                </ul>
              </div>

              <button
                v-if="show_close_btn"
                class="close-btn"
                @click="$emit('close')"
              >
                <i class="fa-regular fa-circle-xmark fa-xl"></i>
              </button>
            </slot>
          </div>
        </div>
      </div>
    </div>

    <!-- Nota/legenda opcional, por baixo do título -->
    <div v-if="table_subtitle" class="table_subtitle">{{ table_subtitle }}</div>

    <!-- Área livre por cima da tabela (ex.: sumários/totais) -->
    <slot name="table-summary"></slot>

    <!-- DataTable Component - Always rendered in server-side mode -->
    <div class="data-table-container">
      <DataTable
        ref="dataTable"
        :data="currentTableData"
        :columns="dataTablesColumns"
        :options="dataTablesOptions"
        :class="['data-table', { responsive: responsive, nowrap: !responsive }]"
        @select="handleSelect"
        @deselect="handleDeselect"
      />
    </div>

    <!-- Empty State (Client-side mode only) -->
    <div
      v-if="!loading && currentTableData.length === 0 && !useServerSide"
      class="empty-state"
    >
      Nenhum registro encontrado
    </div>
  </div>
</template>

<script>
import axios from "axios";
import DataTable from "datatables.net-vue3";
import DataTablesCore from "datatables.net-dt";
import registerGoToPageFeature from "./goToPageFeature";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";
import "datatables.net-responsive-dt";
import "datatables.net-select-dt";
import "datatables.net-buttons-dt";
import "datatables.net-buttons/js/buttons.html5";
import "datatables.net-buttons/js/buttons.print";
import jszip from "jszip";
import "pdfmake/build/pdfmake";
import "pdfmake/build/vfs_fonts";
import { ta } from "date-fns/locale";

// Initialize DataTables with plugins
DataTable.use(DataTablesCore);

// Caixa "Ir para página" ao lado dos botões de paginação
registerGoToPageFeature(DataTablesCore);

window.JSZip = jszip;

export default {
  name: "GenericDataTableV2",
  components: {
    DataTable,
    Multiselect,
  },

  props: {
    // Display props
    table_title: {
      type: String,
      default: "Tabela de Dados",
    },
    // Nota curta apresentada por baixo do título (legenda da tabela)
    table_subtitle: {
      type: String,
      default: "",
    },
    show: {
      type: Boolean,
      default: true,
    },
    visibility: {
      type: Boolean,
      default: true,
    },

    // API Configuration
    apiUrl: {
      type: String,
      required: true,
    },
    columns: {
      type: Array,
      required: true,
    },

    // Feature toggles
    show_export: {
      type: Boolean,
      default: true,
    },
    // Base name (without extension) of the files generated by the export buttons
    export_filename: {
      type: String,
      default: "export",
    },
    isTransferMode: {
      type: Boolean,
      default: false,
    },
    show_close_btn: {
      type: Boolean,
      default: false,
    },
    show_filter_toggle: {
      type: Boolean,
      default: false,
    },
    show_groupby_btn: {
      type: Boolean,
      default: false,
    },
    groupbyActive: {
      type: Boolean,
      default: false,
    },
    filterVisible: {
      type: Boolean,
      default: false,
    },

    // Initial parameters
    initialParams: {
      type: Object,
      default: () => ({}),
    },

    // DataTables server-side processing
    useServerSide: {
      type: Boolean,
      default: true,
    },

    // Responsive DataTables
    responsive: {
      type: Boolean,
      default: false,
    },

    // Data for client-side mode
    tableData: {
      type: Array,
      default: () => [],
    },

    // Footer slot visibility
    showFooter: {
      type: Boolean,
      default: false,
    },

    // Drag and drop support
    draggable: {
      type: Boolean,
      default: false,
    },

    // Ordering configuration
    preserveApiOrder: {
      type: Boolean,
      default: false,
    },
    defaultOrderColumn: {
      type: Number,
      default: null,
    },
    defaultOrderDirection: {
      type: String,
      default: "asc",
      validator: (value) => ["asc", "desc"].includes(value),
    },

    // Row selection configuration
    selectable: {
      type: Boolean,
      default: false,
    },
    selectionMode: {
      type: String,
      default: "multi",
      validator: (value) =>
        ["single", "multi", "os", "multi+shift"].includes(value),
    },
    selectedRows: {
      type: Array,
      default: () => [],
    },
    // Function to determine if a row can be selected
    // Receives row data, returns true if selectable
    rowSelectable: {
      type: Function,
      default: null,
    },

    // Scroll configuration
    scrollY: {
      type: String,
      default: "60vh",
    },

    // Paginação: false mostra todas as linhas (sem paginação nem "Mostrar N registos")
    paging: {
      type: Boolean,
      default: true,
    },
    // Ordenação pelos cabeçalhos (false mantém a ordem dos dados, ex.: linhas de subtotal)
    ordering: {
      type: Boolean,
      default: true,
    },
    // Classe(s) CSS extra por linha: (row) => string | null
    rowClass: {
      type: Function,
      default: null,
    },

    // Editable columns configuration
    editableColumns: {
      type: Array,
      default: () => [],
    },

    // Tipos de acção que apenas navegam (ex.: ["viewDetalhes", "viewArtigos"]).
    // Só estas respondem a clique do scroll / Ctrl+clique com "abrir em novo
    // separador"; as restantes (editar, apagar, ...) têm efeitos secundários e
    // não devem ser disparadas por engano.
    navActions: {
      type: Array,
      default: () => [],
    },

    // Ligar só nas tabelas em que o clique na linha navega para outra rota.
    // Na maioria abre um formulário na própria página, e aí o gesto não faz
    // sentido — daí ser opt-in.
    rowClickNavigates: {
      type: Boolean,
      default: false,
    },

    // Nas listagens de negócios o clique normal na linha abre o registo num
    // separador novo, para não se perder a listagem (filtros, pesquisa e
    // página onde se ia). Precisa de `rowClickNavigates`.
    rowClickNewTab: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // Data
      internalTableData: [],
      loading: false,
      
      // UI State
      isExportOpen: false,
      isExporting: false,
      isTransferSearchOpen: false,
      // Transfer mode - Cliente selection (step 1)
      transferTargetCliente: null,
      clientesFiltered: [],
      clientesLoading: false,
      clienteSearchTimeout: null,

      // Transfer mode - SubCliente selection (step 2)
      transferTargetSubcliente: null,
      subclientesFiltered: [],
      subclientesLoading: false,
      subclienteSearchTimeout: null,

      // Request management
      isReloading: false,
      lastInitialParams: null,

      // Row selection
      internalSelectedRows: [],
      allSelected: false,
    };
  },

  computed: {
    // Process columns to match DataTable format
    dataTablesColumns() {
      const columns = [];

      // Add regular columns
      this.columns.forEach((col) => {
        const columnDef = {
          data: col.key,
          title: col.label,
          orderable: col.sortable !== false,
          searchable: col.searchable !== false,
          width: col.width || null,
          className: col.className || "",
          defaultContent: "",
          visible: col.visible !== false, // Hide column if visible is false
        };

        // Only add render if it exists and is not null
        const renderFn = col.render || this.createDataTablesRender(col);
        if (renderFn !== null && renderFn !== undefined) {
          columnDef.render = renderFn;
        }

        // Add max-width styling
        if (col.maxWidth) {
          const maxWidthValue =
            typeof col.maxWidth === "string"
              ? col.maxWidth
              : col.maxWidth + "px";

          columnDef.className += ` max-width-${col.maxWidth}`;
          columnDef.width = maxWidthValue;

          const maxWidth = col.maxWidth;
          columnDef.createdCell = function (
            td,
            cellData,
            rowData,
            row,
            colIndex,
          ) {
            const maxWidthValue =
              typeof maxWidth === "string" ? maxWidth : maxWidth + "px";
            td.style.maxWidth = maxWidthValue;
            td.style.minWidth = maxWidthValue;
            td.style.width = maxWidthValue;
          };
        }

        columns.push(columnDef);
      });

      return columns;
    },

    dataTablesOptions() {
      const options = {
        processing: true,
        serverSide: this.useServerSide,
        responsive: this.responsive,
        pageLength: 25,
        lengthMenu: [10, 25, 50, 100],
        scrollY: this.scrollY,
        scrollX: true,
        scrollCollapse: true,
        autoWidth: false,
        deferRender: true,
        stateSave: false,
        paging: this.paging,
        searching: true,
        ordering: this.ordering,
        info: true,
        // Paginação + caixa para saltar diretamente para uma página
        layout: this.paging
          ? { bottomEnd: ["paging", "goToPage"] }
          : { topStart: null, bottomEnd: null },
        language: {
          lengthMenu: "Mostrar _MENU_ registros por página",
          zeroRecords: "Nenhum registro encontrado",
          info: "Mostrando _START_ a _END_ de _TOTAL_ registros",
          infoEmpty: "Mostrando 0 a 0 de 0 registros",
          infoFiltered: "(filtrado de _MAX_ registros no total)",
          infoThousands: ".",
          select: {
            rows: {
              _: "%d linhas selecionadas",
              0: "",
              1: "1 linha selecionada",
            },
          },
          search: "Pesquisar:",
          emptyTable: "Nenhum dado disponível na tabela",
          paginate: {
            first: "Primeiro",
            last: "Último",
            next: "Próximo",
            previous: "Anterior",
          },
          processing: "Processando...",
        },
        buttons: [
          {
            extend: "copyHtml5",
            text: "Copiar",
            exportOptions: this.getExportOptions(),
          },
          {
            extend: "csvHtml5",
            text: "CSV",
            filename: this.export_filename,
            exportOptions: this.getExportOptions(),
          },
          {
            extend: "excelHtml5",
            text: "Excel",
            filename: this.export_filename,
            exportOptions: this.getExportOptions(),
          },
          {
            extend: "pdfHtml5",
            text: "PDF",
            filename: this.export_filename,
            orientation: "landscape",
            pageSize: "A3",
            exportOptions: this.getExportOptions(),
            customize: (doc) => {
              // Default text style
              doc.defaultStyle.fontSize = 8;
              doc.defaultStyle.color = "#000000"; // Black text for body

              // Table header styling
              doc.styles.tableHeader.fontSize = 9;
              doc.styles.tableHeader.fillColor = "#2d3748"; // Dark gray background
              doc.styles.tableHeader.color = "#ffffff"; // White text
              doc.styles.tableHeader.bold = true;

              // Table body styling
              doc.styles.tableBodyEven = doc.styles.tableBodyEven || {};
              doc.styles.tableBodyOdd = doc.styles.tableBodyOdd || {};
              doc.styles.tableBodyEven.fillColor = "#f9fafb"; // Light gray for even rows
              doc.styles.tableBodyOdd.fillColor = "#ffffff"; // White for odd rows

              // Set column widths
              if (doc.content[1].table) {
                doc.content[1].table.widths = Array(
                  doc.content[1].table.body[0].length,
                ).fill("*");
              }

              doc.pageMargins = [10, 10, 10, 10];
            },
          },
          {
            extend: "print",
            text: "Imprimir",
            exportOptions: this.getExportOptions(),
          },
        ],
      };

      // Configure ordering
      if (this.preserveApiOrder) {
        options.order = [];
      } else if (this.defaultOrderColumn !== null) {
        options.order = [[this.defaultOrderColumn, this.defaultOrderDirection]];
      }

      // Server-side processing
      if (this.useServerSide) {
        options.ajax = (data, callback, settings) => {
          this.fetchServerData(data, callback);
        };
      }

      // Row selection
      if (this.selectable) {
        // Configure DataTables Select plugin
        options.select = {
          style: this.selectionMode,
          selector: "td:not(.action-column)",
          className: "selected-row",
          info: true,
        };
      }

      // Row callback for draggable and non-selectable rows
      if (this.draggable || this.rowSelectable || this.rowClass) {
        options.rowCallback = (row, data) => {
          // Classes extra pedidas pela página (ex.: subtotais)
          if (this.rowClass) {
            const extra = this.rowClass(data.__rawRow || data);
            if (extra) row.classList.add(...String(extra).split(/\s+/).filter(Boolean));
          }

          // Apply non-selectable styling if rowSelectable function returns false
          if (this.rowSelectable && !this.rowSelectable(data)) {
            row.classList.add("row-not-selectable");
            row.style.opacity = "0.6";
            row.style.cursor = "not-allowed";
          }

          // Drag and drop functionality
          if (this.draggable) {
            row.setAttribute("draggable", "true");
            row.style.cursor = "move";
            row.classList.add("cursor-move");

            row.addEventListener("dragstart", (e) => {
              row.classList.add("dragging");
              this.$emit("drag-start", { row, data, event: e });
            });

            row.addEventListener("dragend", (e) => {
              row.classList.remove("dragging");
              this.$emit("drag-end", { row, data, event: e });
            });

            row.addEventListener("dragover", (e) => {
              e.preventDefault();
              this.$emit("drag-over", { row, data, event: e });
            });

            row.addEventListener("drop", (e) => {
              e.preventDefault();
              this.$emit("drop", { row, data, event: e });
            });
          }
        };
      }

      return options;
    },

    currentTableData() {
      return this.useServerSide ? this.internalTableData : this.tableData;
    },
  },

  mounted() {
    this.lastInitialParams = JSON.stringify(this.initialParams);

    this.$nextTick(() => {
      this.setupEventListeners();
      this.setupResizeObserver();
    });
  },

  beforeDestroy() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
  },

  methods: {
    setupResizeObserver() {
      const container = this.$el;
      if (!container || typeof ResizeObserver === 'undefined') return;

      let adjustTimeout = null;
      this._resizeObserver = new ResizeObserver(() => {
        clearTimeout(adjustTimeout);
        adjustTimeout = setTimeout(() => {
          const dt = this.getDataTableInstance();
          if (dt) {
            dt.columns.adjust();
          }
        }, 100);
      });

      this._resizeObserver.observe(container);
    },

    getExportOptions() {
      const exportColumns = this.columns
        .map((col, idx) => (col.exportable !== false ? idx : null))
        .filter((idx) => idx !== null);

      return {
        columns: exportColumns,
        format: {
          body: (data, row, column, node) => {
            // Get the column definition
            const colDef = this.columns[column];

            // If column has exportValue function, use it
            if (colDef && typeof colDef.exportValue === "function") {
              try {
                const dt = this.getDataTableInstance();
                if (dt) {
                  // Sempre a linha em bruto: o `exportValue` das colunas lê os
                  // campos da API, não o HTML que a tabela mostra.
                  const rowData = dt.row(row).data();
                  const value = colDef.exportValue(rowData.__rawRow || rowData);
                  return typeof value === "string"
                    ? this.stripHtmlForExport(value)
                    : value;
                }
              } catch (error) {
                console.warn(
                  `Error in exportValue for column ${colDef.key}:`,
                  error,
                );
              }
            }

            // Otherwise strip HTML tags from the data
            if (typeof data === "string") {
              return this.stripHtmlForExport(data);
            }
            return data;
          },
        },
      };
    },

    async fetchServerData(data, callback) {
      try {
        const showAll = data.length === -1;
        const apiParams = {
          page: showAll ? 1 : Math.floor(data.start / data.length) + 1,
          page_size: showAll ? 50000 : data.length,
          search: data.search.value || "",
          ...this.initialParams,
        };

        // Add ordering
        if (data.order && data.order.length > 0) {
          const orderColIndex = data.order[0].column;
          const orderDir = data.order[0].dir;
          const orderCol = data.columns[orderColIndex];

          if (orderCol && orderCol.data) {
            // Computed/virtual columns can declare `sortKey` to order by a
            // real backend field instead of the column key
            const colDef = this.columns.find((c) => c.key === orderCol.data);
            const sortField = colDef?.sortKey || orderCol.data;
            apiParams.order_by =
              orderDir === "desc" ? `-${sortField}` : sortField;
          }
        }

        const response = await axios.get(this.apiUrl, { params: apiParams });
        const json = response.data;
        const recordsTotal = json.total_count || json.count || 0;
        const recordsFiltered = json.total_count || json.count || 0;
        const results = json.results || json.data || [];

        const processedResults = this.processTableData(results);

        // Payload completo da API (inclui extras como totais/agregados) para
        // quem consome a tabela poder mostrar sumários sincronizados com os
        // filtros activos.
        this.$emit("data-loaded", json);

        callback({
          draw: data.draw,
          recordsTotal: recordsTotal,
          recordsFiltered: recordsFiltered,
          data: processedResults,
        });
      } catch (error) {
        console.error("DataTables Error:", error);
        this.$emit("data-error", error);
        callback({
          draw: data.draw,
          recordsTotal: 0,
          recordsFiltered: 0,
          data: [],
        });
      }
    },

    processTableData(data) {
      return data.map((item) => {
        const processedItem = { ...item };
        // A linha como veio da API: os campos do `processedItem` passam a ter o
        // HTML das colunas `computed`, e a exportação precisa dos originais.
        Object.defineProperty(processedItem, "__rawRow", {
          value: item,
          enumerable: false,
        });

        this.columns.forEach((column) => {
          if (column.type === "actions") {
            processedItem[column.key] = "";
            return;
          }

          if (column.computed && typeof column.computed === "function") {
            try {
              processedItem[column.key] = column.computed(item);
            } catch (error) {
              console.warn(
                `Error in computed function for column ${column.key}:`,
                error,
              );
              processedItem[column.key] = "N/A";
            }
          } else if (column.key && column.key.includes(".")) {
            processedItem[column.key] = this.getValueByPath(item, column.key);
          } else if (column.key && !processedItem.hasOwnProperty(column.key)) {
            const nestedValue = this.getValueByPath(
              item,
              column.key.replace("_", "."),
            );
            if (nestedValue !== null) {
              processedItem[column.key] = nestedValue;
            } else {
              processedItem[column.key] = item[column.key] || "";
            }
          }
        });

        return processedItem;
      });
    },

    createDataTablesRender(column) {
      if (!column.type || column.type === "text") {
        return null;
      }

      return (data, type, row, meta) => {
        if (type === "display") {
          switch (column.type) {
            case "actions":
              return this.generateActionsHTML(column, row);
            case "badge":
              const value = this.getValueByPath(row, column.key);
              return this.generateBadgeHTML(value, column, row);
            case "custom-link":
              const linkValue = this.getValueByPath(row, column.key);
              if (!linkValue) {
                return `<span class="text-gray-400">${column.noValueText || "N/A"}</span>`;
              }
              const linkAction = column.linkAction || "view";
              return `<span class="cursor-pointer text-blue-400 hover:text-blue-600 underline" data-action="${linkAction}" data-item-id="${row.id}">${linkValue}</span>`;
            case "boolean":
              const boolValue = this.getValueByPath(row, column.key);
              // Check if this column is editable
              const isEditable =
                this.editableColumns.includes(column.key) ||
                column.editable === true;
              if (isEditable) {
                return this.generateBooleanToggleHTML(boolValue, row, column);
              } else {
                return this.formatColumnValue(boolValue, column.type);
              }
            case "number-input":
              const numValue = this.getValueByPath(row, column.key);
              const isNumEditable =
                this.editableColumns.includes(column.key) ||
                column.editable === true;
              if (isNumEditable) {
                return this.generateNumberInputHTML(numValue, row, column);
              } else {
                return this.formatColumnValue(numValue, "number");
              }
            case "date":
            case "datetime":
            case "currency":
            case "number":
            case "percentage":
            case "truncate":
              const formattedValue = this.getValueByPath(row, column.key);
              return this.formatColumnValue(formattedValue, column.type);
            default:
              const defaultValue = this.getValueByPath(row, column.key);
              return this.formatColumnValue(defaultValue, column.type);
          }
        }
        return data;
      };
    },

    generateActionsHTML(column, item) {
      const actions = this.getColumnActions(column);
      let html = '<div class="actions-cell-wrapper">';
      let visibleActions = 0;

      actions.forEach((action) => {
        if (action.showIf && !this.evaluateCondition(action.showIf, item)) {
          return;
        }
        if (action.hideIf && this.evaluateCondition(action.hideIf, item)) {
          return;
        }

        const btnClass =
          typeof action.class === "function"
            ? action.class(item)
            : action.class || this.getDefaultActionClass(action.type);
        const icon = action.icon || this.getDefaultActionIcon(action.type);
        const text = action.text || this.getDefaultActionText(action.type);
        const title =
          typeof action.title === "function"
            ? action.title(item)
            : action.title || this.getDefaultActionTitle(action.type);

        html += `<button class="action-cell-btn ${btnClass}" data-action="${
          action.type
        }" data-item-id="${item.id}" title="${title}">
                   <i class="${icon}"></i>
                   ${text ? `<span class="action-text">${text}</span>` : ""}
                 </button>`;
        visibleActions++;
      });

      if (visibleActions === 0) {
        html += '<span class="text-gray-400 text-xs">Sem ações</span>';
      }

      html += "</div>";
      return html;
    },

    generateBadgeHTML(value, column, row) {
      if (value === null || value === undefined || value === "") {
        return "";
      }

      const badgeClass = this.getBadgeClass(value, column, row);
      const formattedValue = this.formatColumnValue(value, "badge");

      let html = `<span class="${badgeClass}">${formattedValue}</span>`;

      // Optional clickable icon before the badge. Column config:
      // prefixIcon: (row) => ({ html, action, title }) | null
      if (row && typeof column.prefixIcon === "function") {
        let icon = null;
        try {
          icon = column.prefixIcon(row);
        } catch (error) {
          console.warn(`Error in prefixIcon for column ${column.key}:`, error);
        }
        if (icon && icon.html) {
          html =
            `<span class="cursor-pointer mr-1 inline-block hover:scale-110 transition-transform" data-action="${icon.action || "prefixIconClick"}" data-item-id="${row.id}" title="${icon.title || ""}">${icon.html}</span>` +
            html;
        }
      }

      return html;
    },

    generateBooleanToggleHTML(value, row, column) {
      const isChecked = value ? "checked" : "";
      const displayValue = value ? "Sim" : "Não";
      const rowId = row.id;
      const columnKey = column.key;

      return `
        <div class="boolean-toggle-wrapper">
          <label class="switch">
            <input
              type="checkbox"
              ${isChecked}
              data-boolean-toggle="true"
              data-row-id="${rowId}"
              data-column-key="${columnKey}"
            />
            <span class="slider round"></span>
          </label>
          <span class="boolean-value-text">${displayValue}</span>
        </div>
      `;
    },

    generateNumberInputHTML(value, row, column) {
      const rowId = row.id;
      const columnKey = column.key;
      const min = column.min !== undefined ? `min="${column.min}"` : "";
      const maxValue =
        typeof column.max === "function" ? column.max(row) : column.max;
      const max = maxValue !== undefined ? `max="${maxValue}"` : "";
      const step = column.step !== undefined ? column.step : "1";
      const displayValue =
        value !== null && value !== undefined && value !== "" ? value : "";

      return `
        <div class="number-input-wrapper">
          <input
            type="number"
            value="${displayValue}"
            ${min}
            ${max}
            step="${step}"
            data-number-input="true"
            data-row-id="${rowId}"
            data-column-key="${columnKey}"
            class="number-input-field"
          />
        </div>
      `;
    },

    evaluateCondition(condition, item) {
      if (typeof condition === "function") {
        return condition(item);
      }
      if (typeof condition === "string") {
        const isNegated = condition.startsWith("!");
        const propertyName = isNegated ? condition.slice(1) : condition;
        const value = this.getValueByPath(item, propertyName);
        return isNegated ? !value : !!value;
      }
      if (typeof condition === "object") {
        for (const [key, expectedValue] of Object.entries(condition)) {
          const actualValue = this.getValueByPath(item, key);
          if (
            typeof expectedValue === "object" &&
            expectedValue.$exists !== undefined
          ) {
            const exists =
              actualValue !== null &&
              actualValue !== undefined &&
              actualValue !== "";
            if (exists !== expectedValue.$exists) return false;
          } else {
            if (actualValue !== expectedValue) return false;
          }
        }
        return true;
      }
      return false;
    },

    getDefaultActionClass(actionType) {
      const classMap = {
        adicionar:
          "bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-xs",
        create:
          "bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-xs",
        edit: "bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs",
        delete:
          "bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs",
        view: "bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded text-xs",
        download:
          "bg-purple-500 hover:bg-purple-600 text-white px-2 py-1 rounded text-xs",
        alter:
          "bg-yellow-600 hover:bg-yellow-700 text-white px-2 py-1 rounded text-xs",
        confirm:
          "bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs",
      };
      return (
        classMap[actionType] ||
        "bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs"
      );
    },

    getDefaultActionIcon(actionType) {
      const iconMap = {
        adicionar: "fa-solid fa-circle-plus",
        create: "fas fa-plus",
        edit: "fas fa-edit",
        delete: "fas fa-trash",
        view: "fas fa-eye",
        download: "fas fa-download",
        alter: "fas fa-exchange-alt",
        confirm: "fas fa-check",
      };
      return iconMap[actionType] || "fas fa-cog";
    },

    getDefaultActionText(actionType) {
      const textMap = {
        create: "Criar",
        adicionar: "Adicionar",
        edit: "",
        delete: "",
        view: "",
        download: "Download",
        alterar: "Alterar",
        confirmar: "Confirmar",
        adiar: "Adiar",
      };
      return textMap[actionType] || "";
    },

    getDefaultActionTitle(actionType) {
      const titleMap = {
        create: "Criar novo",
        edit: "Editar",
        delete: "Apagar",
        view: "Visualizar",
        download: "Fazer download",
      };
      return titleMap[actionType] || actionType;
    },

    getColumnActions(column) {
      if (!column.actions) return [];

      return column.actions.map((action) => {
        if (typeof action === "string") {
          const actionMap = {
            edit: {
              type: "edit",
              icon: "fas fa-edit",
              class:
                "bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs",
              title: "Editar",
            },
            delete: {
              type: "delete",
              icon: "fas fa-trash",
              class:
                "bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs",
              text: "Apagar",
              title: "Apagar Termo",
            },
            create: {
              type: "create",
              icon: "fa-solid fa-circle-plus",
              class:
                "bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-xs",
              text: "Criar",
              title: "Criar Termo",
            },
            view: {
              type: "view",
              icon: "fas fa-eye",
              class:
                "bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-xs",
              title: "Ver",
            },
          };
          return (
            actionMap[action] || {
              type: action,
              icon: "fas fa-cog",
              class:
                "bg-gray-500 hover:bg-gray-600 text-[var(--text)] px-2 py-1 rounded text-xs",
              title: action,
            }
          );
        }
        return action;
      });
    },

    formatDate(date, includeTime = false) {
      const day = String(date.getUTCDate()).padStart(2, "0");
      const monthNames = [
        "Jan",
        "Fev",
        "Mar",
        "Abr",
        "Mai",
        "Jun",
        "Jul",
        "Ago",
        "Set",
        "Out",
        "Nov",
        "Dez",
      ];
      const month = monthNames[date.getUTCMonth()];
      const year = date.getUTCFullYear();

      if (includeTime) {
        const hours = String(date.getUTCHours()).padStart(2, "0");
        const minutes = String(date.getUTCMinutes()).padStart(2, "0");
        return `${day} ${month} ${year} ${hours}:${minutes}`;
      }

      return `${day} ${month} ${year}`;
    },

    formatColumnValue(value, type) {
      if (value === null || value === undefined || value === "") return "";

      switch (type) {
        case "date":
          try {
            return this.formatDate(new Date(value), false);
          } catch {
            return String(value);
          }

        case "datetime":
          try {
            return this.formatDate(new Date(value), true);
          } catch {
            return String(value);
          }

        case "currency":
          try {
            return new Intl.NumberFormat("pt-PT", {
              style: "currency",
              currency: "EUR",
            }).format(parseFloat(value));
          } catch {
            return String(value);
          }

        case "number":
          try {
            return new Intl.NumberFormat("pt-PT").format(parseFloat(value));
          } catch {
            return String(value);
          }

        case "percentage":
          try {
            return new Intl.NumberFormat("pt-PT", {
              style: "percent",
              minimumFractionDigits: 1,
              maximumFractionDigits: 2,
            }).format(parseFloat(value) / 100);
          } catch {
            return String(value);
          }

        case "boolean":
          return value ? "Sim" : "Não";

        case "badge":
          return String(value);

        case "truncate":
          const str = String(value);
          return str.length > 50 ? str.substring(0, 50) + "..." : str;

        default:
          return String(value);
      }
    },

    getBadgeClass(value, column, row) {
      const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";

      // Cor dependente da linha e não do valor. Column config:
      // badgeClass: (row, value) => "bg-red-600/80 text-white" | null
      if (typeof column.badgeClass === "function") {
        let customClass = null;
        try {
          customClass = column.badgeClass(row, value);
        } catch (error) {
          console.warn(`Error in badgeClass for column ${column.key}:`, error);
        }
        if (customClass) {
          return `${baseClasses} ${customClass}`;
        }
      }

      if (!column.badgeColors) {
        // Special handling for numeric badges (e.g., movimentos)
        if (typeof value === "number" && column.key === "movimentos") {
          return value > 0
            ? `${baseClasses} bg-red-600/80 text-white`
            : `${baseClasses} bg-green-500/20 text-green-300`;
        }

        // Special handling for stock quantity with gradient levels
        if (typeof value === "number" && column.key === "quantidade") {
          if (value === 0) {
            // Out of stock - critical
            return `${baseClasses} bg-red-600/80 text-white`;
          } else if (value > 0 && value <= 5) {
            // Low stock - warning
            return `${baseClasses} bg-yellow-500/60 text-yellow-100`;
          } else if (value > 5 && value <= 10) {
            // Medium stock - moderate
            return `${baseClasses} bg-blue-500/40 text-blue-200`;
          } else {
            // Good stock - healthy
            return `${baseClasses} bg-green-500/20 text-green-300`;
          }
        }

        const defaultColors = {
          inativo: "bg-red-500/20 text-red-300",
          desativado: "bg-red-500/20 text-red-300",
          bloqueado: "bg-red-500/20 text-red-300",
          rejeitado: "bg-red-500/20 text-red-300",
          planeamento: "bg-yellow-500/20 text-yellow-300",
          eliminado: "bg-gray-500/20 text-gray-300",
          sim: "bg-green-500/20 text-green-300",
          não: "bg-red-500/20 text-red-300",
          ativo: "bg-green-400/20 text-green-300",
          vendido: "bg-green-400/20 text-green-300",
          reservado: "bg-yellow-600/20 text-yellow-200",
          aprovado: "bg-green-700/60 text-green-400",
          atribuido: "bg-purple-600/20 text-purple-300",
          confirmado: "bg-green-500/20 text-green-300",
          cancelado: "bg-gray-500/20 text-gray-300",
          fechado: "bg-green-600/20 text-green-300",
          interrompido: "bg-orange-600/20 text-orange-200",
          pendente: "bg-yellow-500/20 text-yellow-300",
          remarcar: "bg-red-500/20 text-red-300",
          execução: "bg-green-600/20 text-green-300",
          transferido: "bg-yellow-600/20 text-yellow-200",
          agendado: "bg-yellow-500/20 text-yellow-300",
          create: "bg-green-600/20 text-green-300",
          update: "bg-blue-600/20 text-blue-300",
          delete: "bg-red-600/20 text-red-300",
          bulk_create: "bg-green-600/20 text-green-300",
          bulk_update: "bg-blue-600/20 text-blue-300",
          bulk_delete: "bg-red-600/20 text-red-300",
          equipamento: "bg-blue-600/20 text-blue-300",
          acessorio: "bg-purple-600/20 text-purple-300",
          independente: "bg-yellow-600/20 text-yellow-300",
        };

        return `${baseClasses} ${
          defaultColors[String(value).toLowerCase()] || "bg-gray-400 text-[var(--text)]"
        }`;
      }

      const colorKey = String(value).toLowerCase();
      const colorClass =
        column.badgeColors[colorKey] ||
        column.badgeColors[value] ||
        "secondary";

      const colorMap = {
        success: "bg-green-500 text-white",
        danger: "bg-red-500 text-white",
        warning: "bg-yellow-500 text-black",
        info: "bg-blue-500 text-white",
        secondary: "bg-gray-500 text-[var(--text)]",
        primary: "bg-indigo-500 text-white",
      };

      return `${baseClasses} ${
        colorMap[colorClass] || "bg-gray-400 text-[var(--text)]"
      }`;
    },

    getValueByPath(obj, path) {
      if (!obj || !path) return null;

      const keys = path.split(".");
      let value = obj;

      for (const key of keys) {
        if (key.includes("[") && key.includes("]")) {
          const arrayKey = key.substring(0, key.indexOf("["));
          const index = parseInt(
            key.substring(key.indexOf("[") + 1, key.indexOf("]")),
          );

          if (
            value[arrayKey] &&
            Array.isArray(value[arrayKey]) &&
            value[arrayKey][index] !== undefined
          ) {
            value = value[arrayKey][index];
          } else {
            return null;
          }
        } else {
          if (value && typeof value === "object" && value.hasOwnProperty(key)) {
            value = value[key];
          } else {
            return null;
          }
        }
      }

      return value;
    },

    isNavAction(actionType) {
      return !!actionType && this.navActions.includes(actionType);
    },

    // Corre `emitFn` com o router.push interceptado: em vez de navegar no
    // separador actual, resolve o destino e abre-o num separador novo.
    //
    // Isto evita ter de declarar o URL de cada linha na configuração de cada
    // tabela — as views continuam a chamar `this.$router.push({...})` como
    // sempre, sem saberem do gesto. Funciona porque os $emit do Vue são
    // síncronos: o handler da view corre dentro da chamada abaixo, com o push
    // ainda substituído.
    //
    // Limitação: um handler que só faça push depois de um `await` não é
    // apanhado (o push original já foi reposto) e navega normalmente.
    emitInNewTab(emitFn) {
      const router = this.$router;
      if (!router) {
        emitFn();
        return;
      }

      const originalPush = router.push;
      let opened = false;

      router.push = (to) => {
        try {
          const resolved = router.resolve(to);
          window.open(resolved.href, "_blank");
          opened = true;
        } catch (error) {
          console.warn("Não foi possível abrir em separador novo:", error);
          return originalPush.call(router, to);
        }
        return Promise.resolve();
      };

      try {
        emitFn();
      } finally {
        router.push = originalPush;
      }

      return opened;
    },

    setupEventListeners() {
      const dt = this.getDataTableInstance();
      if (!dt) return;

      // Elementos dentro da linha que têm comportamento próprio e nunca devem
      // disparar o row-click.
      const isInteractiveTarget = (target) =>
        !!(
          target.closest("button[data-action], span[data-action]") ||
          // Links dentro da linha (ex.: o nº da proposta, que abre o PDF)
          // levam o utilizador para outro sítio e valem por si
          target.closest("a[href]") ||
          target.closest(".boolean-toggle-wrapper") ||
          target.closest(".number-input-wrapper")
        );

      const getRowData = (rowElement) => {
        const rowData = dt.row(rowElement).data();
        return rowData && Object.keys(rowData).length > 0 ? rowData : null;
      };

      // Row click events
      dt.on("click", "tbody tr", (event) => {
        if (isInteractiveTarget(event.target)) return;

        const rowData = getRowData(event.currentTarget);
        if (!rowData) return;

        // Separador novo: sempre, nas tabelas com `rowClickNewTab`; nas
        // outras só com Ctrl/Cmd, como num link normal.
        if (
          this.rowClickNavigates &&
          (this.rowClickNewTab || event.ctrlKey || event.metaKey)
        ) {
          event.preventDefault();
          this.emitInNewTab(() => this.$emit("row-click", rowData));
          return;
        }

        this.$emit("row-click", rowData);
      });

      // O clique do scroll sobre a tabela activa o autoscroll do browser (o
      // círculo de pan) no Windows, que engole o mouseup seguinte e impede o
      // "auxclick" de disparar de forma fiável. Cancelar o default no mousedown
      // evita que o autoscroll arranque, para o "auxclick" chegar sempre.
      dt.on("mousedown", "tbody tr", (event) => {
        if (event.button !== 1) return;
        if (isInteractiveTarget(event.target)) {
          const element = event.target.closest(
            "button[data-action], span[data-action]"
          );
          if (!this.isNavAction(element?.getAttribute("data-action"))) return;
        } else if (!this.rowClickNavigates) {
          return;
        }
        event.preventDefault();
      });

      // Clique do scroll na linha — abrir em separador novo
      dt.on("auxclick", "tbody tr", (event) => {
        if (event.button !== 1) return;
        if (!this.rowClickNavigates) return;
        if (isInteractiveTarget(event.target)) return;

        const rowData = getRowData(event.currentTarget);
        if (!rowData) return;

        event.preventDefault();
        this.emitInNewTab(() => this.$emit("row-click", rowData));
      });

      // Helper to build enriched row data for action handlers
      const buildEnrichedRowData = (element) => {
        const row = dt.row(element.closest("tr"));
        const rowData = row.data();
        const rowNode = row.node();
        const numberInputs = rowNode.querySelectorAll("input[data-number-input]");
        const inputValues = {};
        numberInputs.forEach((input) => {
          const columnKey = input.getAttribute("data-column-key");
          inputValues[columnKey] = parseFloat(input.value) || 0;
        });
        return { ...rowData, ...inputValues };
      };

      const emitAction = (element, extra = {}) => {
        const actionType = element.getAttribute("data-action");
        const enrichedRowData = buildEnrichedRowData(element);
        this.$emit("action", {
          type: actionType,
          item: enrichedRowData,
          ...extra,
        });
        this.$emit(actionType, enrichedRowData);
      };

      // Action button clicks (left click)
      dt.on("click", "button[data-action], span[data-action]", (event) => {
        event.stopPropagation();
        const element = event.currentTarget;

        // Ctrl/Cmd+clique abre em separador novo, como num link normal.
        if (
          this.isNavAction(element.getAttribute("data-action")) &&
          (event.ctrlKey || event.metaKey)
        ) {
          event.preventDefault();
          this.emitInNewTab(() => emitAction(element));
          return;
        }

        emitAction(element);
      });

      // Clique do scroll num botão de acção — abrir em separador novo. Só para
      // as acções declaradas em navActions: as restantes têm efeitos
      // secundários (abrir formulário, apagar) e não devem correr por engano.
      dt.on("auxclick", "button[data-action], span[data-action]", (event) => {
        if (event.button !== 1) return;
        const element = event.currentTarget;
        if (!this.isNavAction(element.getAttribute("data-action"))) return;

        event.stopPropagation();
        event.preventDefault();
        this.emitInNewTab(() => emitAction(element, { middleClick: true }));
      });

      // Boolean toggle clicks
      dt.on("change", "input[data-boolean-toggle]", (event) => {
        event.stopPropagation();
        const input = event.currentTarget;
        const rowId = input.getAttribute("data-row-id");
        const columnKey = input.getAttribute("data-column-key");
        const newValue = input.checked;

        // Get the full row data
        const row = dt.row(input.closest("tr"));
        const rowData = row.data();

        // Update the display text
        const textSpan = input
          .closest(".boolean-toggle-wrapper")
          .querySelector(".boolean-value-text");
        if (textSpan) {
          textSpan.textContent = newValue ? "Sim" : "Não";
        }

        // Emit event to parent
        this.$emit("boolean-toggle", {
          rowId: rowId,
          columnKey: columnKey,
          newValue: newValue,
          rowData: rowData,
        });
      });

      // Number input changes
      dt.on("change blur", "input[data-number-input]", (event) => {
        event.stopPropagation();
        const input = event.currentTarget;
        const rowId = input.getAttribute("data-row-id");
        const columnKey = input.getAttribute("data-column-key");
        const newValue = parseFloat(input.value) || 0;

        // Get the full row data
        const row = dt.row(input.closest("tr"));
        const rowData = row.data();

        // Emit event to parent
        this.$emit("number-input-change", {
          rowId: rowId,
          columnKey: columnKey,
          newValue: newValue,
          rowData: rowData,
        });
      });

      // Row selection is now handled by DataTables Select plugin
      // Users can click on rows to select/deselect them
    },

    handleSelect(event, dt, type, indexes) {
      if (type === "row") {
        // Check if rowSelectable function is provided
        if (this.rowSelectable) {
          // Get the newly selected rows
          indexes.forEach((index) => {
            const rowData = dt.row(index).data();
            // If row is not selectable, deselect it
            if (!this.rowSelectable(rowData)) {
              dt.row(index).deselect();
            }
          });
        }

        // Get all currently selected rows
        const selectedData = dt.rows({ selected: true }).data().toArray();

        // Update internal state and emit events
        this.internalSelectedRows = selectedData;
        this.$emit("update:selectedRows", selectedData);
        this.$emit("selection-changed", selectedData);
      }
    },

    handleDeselect(event, dt, type, indexes) {
      if (type === "row") {
        const selectedData = dt.rows({ selected: true }).data().toArray();
        this.internalSelectedRows = selectedData;
        this.allSelected = false;
        this.$emit("update:selectedRows", selectedData);
        this.$emit("selection-changed", selectedData);
      }
    },

    toggleExportDropdown() {
      this.isExportOpen = !this.isExportOpen;
    },
    // ========== CLIENTE SEARCH (Step 1) ==========
    buscarClientes(searchQuery) {
      if (!searchQuery || searchQuery.length < 3) {
        this.clientesFiltered = [];
        return;
      }

      if (this.clienteSearchTimeout) {
        clearTimeout(this.clienteSearchTimeout);
      }

      this.clienteSearchTimeout = setTimeout(() => {
        this.executarBuscaClientes(searchQuery);
      }, 500);
    },

    async executarBuscaClientes(searchQuery) {
      this.clientesLoading = true;

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_URL_BASE}/api/v1/cliente/s/cliente/${searchQuery}/`,
        );

        if (response.data?.status === 200 && Array.isArray(response.data?.data)) {
          this.clientesFiltered = response.data.data;
        } else {
          this.clientesFiltered = [];
        }
      } catch (error) {
        console.error("Erro ao buscar clientes:", error);
        this.clientesFiltered = [];
      } finally {
        this.clientesLoading = false;
      }
    },

    // Called when cliente is selected - clears subcliente and enables the field
    onClienteSelected(selectedCliente) {
      // Clear previous subcliente selection
      this.transferTargetSubcliente = null;
      this.subclientesFiltered = [];

      // If cliente was cleared, do nothing more
      if (!selectedCliente) {
        this.transferTargetCliente = null;
        return;
      }

      // Cliente is now selected, user can now search for subclientes
      console.log('Cliente selecionado:', selectedCliente);
    },

    // ========== SUBCLIENTE SEARCH (Step 2) ==========
    buscarSubClientes(searchQuery) {
      // Require cliente to be selected first
      if (!this.transferTargetCliente) {
        this.subclientesFiltered = [];
        return;
      }

      if (!searchQuery || searchQuery.length < 3) {
        this.subclientesFiltered = [];
        return;
      }

      if (this.subclienteSearchTimeout) {
        clearTimeout(this.subclienteSearchTimeout);
      }

      this.subclienteSearchTimeout = setTimeout(() => {
        this.executarBuscaSubClientes(searchQuery);
      }, 500);
    },

    async executarBuscaSubClientes(searchQuery) {
      if (!this.transferTargetCliente) {
        return;
      }

      this.subclientesLoading = true;

      try {
        // Filter subclientes by cliente_id using the correct endpoint format
        // URL: /api/v1/subcliente/s/cliente_id/{cliente_id}/?exact=1
        const response = await axios.get(
          `${process.env.VUE_APP_URL_BASE}/api/v1/subcliente/s/cliente_id/${this.transferTargetCliente.id}/`,
          {
            params: {
              exact: 1
            }
          }
        );

        if (response.data?.status === 200 && Array.isArray(response.data?.data)) {
          // Filter by search query on the client side
          const allSubclientes = response.data.data;
          this.subclientesFiltered = allSubclientes.filter(sub =>
            sub.nome?.toLowerCase().includes(searchQuery.toLowerCase())
          );
        } else {
          this.subclientesFiltered = [];
        }
      } catch (error) {
        console.error("Erro ao buscar subclientes:", error);
        this.subclientesFiltered = [];
      } finally {
        this.subclientesLoading = false;
      }
    },

    confirmTransfer() {
      const targetSubcliente = this.transferTargetSubcliente;
      const selectedRows = this.getSelectedRows();

      if (!targetSubcliente?.id || selectedRows.length === 0) {
        this.$emit("transfer-invalid", {
          message: "Selecione um subcliente de destino e pelo menos um registo.",
        });
        return;
      }

      this.$emit("transfer-request", {
        targetSubcliente,
        selectedRows,
      });
    },

    async exportData(format) {
      this.isExportOpen = false;

      // For non-server-side mode, use DataTables buttons directly
      if (!this.useServerSide) {
        this.triggerDtButton(format);
        return;
      }

      // Server-side: fetch ALL records from API, then export directly
      this.isExporting = true;
      try {
        const apiParams = {
          page: 1,
          page_size: 50000,
          search: '',
          ...this.initialParams,
        };

        const response = await axios.get(this.apiUrl, { params: apiParams });
        const json = response.data;
        // As linhas seguem em bruto: é o `getExportCellValue` que resolve cada
        // coluna. Passá-las por `processTableData` punha o HTML da tabela
        // dentro dos próprios campos e o `exportValue` deixava de ver o valor
        // original (o Nº Proposta saía como o link em HTML).
        const allData = json.results || json.data || [];

        // Get exportable columns
        const exportCols = this.columns.filter(c => c.exportable !== false && c.type !== 'actions');

        if (format === 'xlsx' || format === 'excel') {
          this.exportToXlsx(allData, exportCols);
        } else if (format === 'csv') {
          this.exportToCsv(allData, exportCols);
        } else if (format === 'pdf') {
          this.triggerDtButton(format);
        }
      } catch (error) {
        console.error('Export failed:', error);
        this.$emit('export-error', error);
      } finally {
        this.isExporting = false;
      }
    },

    triggerDtButton(format) {
      const dt = this.getDataTableInstance();
      if (dt && dt.button) {
        const buttonIndexMap = { copy: 0, csv: 1, xlsx: 2, excel: 2, pdf: 3, print: 4 };
        const idx = buttonIndexMap[format];
        if (idx !== undefined) {
          try { dt.button(idx).trigger(); } catch (e) {
            console.warn('Export button trigger failed:', e);
          }
        }
      }
    },

    // Os valores das colunas vêm com o HTML que a tabela mostra (links, ícones
    // de aviso, <br/> a empilhar cliente/sub-cliente). Nas exportações fica só
    // o texto: as tags saem, o <br/> passa a espaço e as entidades voltam ao
    // caractere. Colunas com `exportValue` continuam a mandar no seu valor.
    stripHtmlForExport(value) {
      const str = String(value);
      if (!str.includes('<') && !str.includes('&')) return str;
      return str
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#0?39;/gi, "'")
        .replace(/&amp;/gi, '&')
        .replace(/\s+/g, ' ')
        .trim();
    },

    // Valor de uma célula para a exportação, a partir da linha em bruto da API.
    // O `exportValue` da coluna manda sempre (é o que dá, por exemplo, números
    // que dêem para somar); senão usa-se o `computed` — o mesmo conteúdo que a
    // tabela mostra — já sem o HTML.
    getExportCellValue(row, col) {
      if (col.exportValue && typeof col.exportValue === 'function') {
        try { return col.exportValue(row); } catch (e) { /* fallback */ }
      }
      let raw;
      if (col.computed && typeof col.computed === 'function') {
        try {
          raw = col.computed(row);
        } catch (e) {
          console.warn(`Error in computed function for column ${col.key}:`, e);
          raw = null;
        }
      } else {
        raw = this.getValueByPath(row, col.key);
        // Chaves como `cliente_nome` que na resposta são `cliente.nome`
        if ((raw === null || raw === undefined) && col.key && !col.key.includes('.')) {
          raw = this.getValueByPath(row, col.key.replace('_', '.'));
        }
      }
      if (raw === null || raw === undefined) return '';
      // Format by type
      switch (col.type) {
        case 'date': case 'datetime': case 'currency': case 'number': case 'percentage': case 'boolean':
          return this.formatColumnValue(raw, col.type);
        default:
          return this.stripHtmlForExport(raw);
      }
    },

    exportToXlsx(data, exportCols) {
      // Build worksheet XML for xlsx using jszip
      const escapeXml = (s) => String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

      let sheetData = '<sheetData>';
      // Header row
      sheetData += '<row r="1">';
      exportCols.forEach((col, ci) => {
        const ref = this.xlsxCellRef(ci, 0);
        sheetData += `<c r="${ref}" t="inlineStr"><is><t>${escapeXml(col.label)}</t></is></c>`;
      });
      sheetData += '</row>';

      // Células com um URL (links de relatório, termo, orçamento…) saem como
      // hiperligação clicável. O Excel aceita no máximo 65 530 por folha;
      // a partir daí o URL fica só como texto.
      const MAX_HYPERLINKS = 65530;
      const hyperlinks = [];

      // Data rows
      data.forEach((row, ri) => {
        sheetData += `<row r="${ri + 2}">`;
        exportCols.forEach((col, ci) => {
          const ref = this.xlsxCellRef(ci, ri + 1);
          const val = this.getExportCellValue(row, col);
          const num = parseFloat(val);
          if (!isNaN(num) && val !== '' && col.type && ['number','currency','percentage'].includes(col.type)) {
            sheetData += `<c r="${ref}"><v>${num}</v></c>`;
          } else if (typeof val === 'string' && /^https?:\/\/\S+$/i.test(val) && hyperlinks.length < MAX_HYPERLINKS) {
            hyperlinks.push({ ref, url: val });
            sheetData += `<c r="${ref}" s="1" t="inlineStr"><is><t>${escapeXml(val)}</t></is></c>`;
          } else {
            sheetData += `<c r="${ref}" t="inlineStr"><is><t>${escapeXml(val)}</t></is></c>`;
          }
        });
        sheetData += '</row>';
      });
      sheetData += '</sheetData>';

      const lastRef = this.xlsxCellRef(exportCols.length - 1, data.length);

      const hyperlinksXml = hyperlinks.length
        ? `<hyperlinks>${hyperlinks.map((h, i) => `<hyperlink ref="${h.ref}" r:id="rIdLink${i + 1}"/>`).join('')}</hyperlinks>`
        : '';

      const sheetXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <dimension ref="A1:${lastRef}"/>
  ${sheetData}
  ${hyperlinksXml}
</worksheet>`;

      const sheetRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${hyperlinks.map((h, i) => `<Relationship Id="rIdLink${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/hyperlink" Target="${escapeXml(h.url)}" TargetMode="External"/>`).join('')}
</Relationships>`;

      // Estilo 1 = hiperligação (azul, sublinhado), usado pelas células com URL.
      const stylesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="2">
    <font><sz val="11"/><name val="Calibri"/></font>
    <font><u/><sz val="11"/><color rgb="FF0563C1"/><name val="Calibri"/></font>
  </fonts>
  <fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
  <borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="2">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>
  </cellXfs>
</styleSheet>`;

      const workbookXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets><sheet name="Export" sheetId="1" r:id="rId1"/></sheets>
</workbook>`;

      const relsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

      const contentTypesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`;

      const topRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

      const zip = new jszip();
      zip.file('[Content_Types].xml', contentTypesXml);
      zip.file('_rels/.rels', topRelsXml);
      zip.file('xl/workbook.xml', workbookXml);
      zip.file('xl/_rels/workbook.xml.rels', relsXml);
      zip.file('xl/worksheets/sheet1.xml', sheetXml);
      zip.file('xl/styles.xml', stylesXml);
      if (hyperlinks.length) {
        zip.file('xl/worksheets/_rels/sheet1.xml.rels', sheetRelsXml);
      }

      zip.generateAsync({ type: 'blob', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        .then(blob => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${this.table_title || 'export'}.xlsx`;
          a.click();
          URL.revokeObjectURL(url);
        });
    },

    xlsxCellRef(col, row) {
      let s = '';
      let c = col;
      do {
        s = String.fromCharCode(65 + (c % 26)) + s;
        c = Math.floor(c / 26) - 1;
      } while (c >= 0);
      return s + (row + 1);
    },

    exportToCsv(data, exportCols) {
      const escapeCsv = (v) => {
        const s = String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const header = exportCols.map(c => escapeCsv(c.label)).join(',');
      const rows = data.map(row =>
        exportCols.map(c => escapeCsv(this.getExportCellValue(row, c))).join(',')
      );
      const csv = '\uFEFF' + [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.table_title || 'export'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },

    getDataTableInstance() {
      return this.$refs.dataTable?.dt;
    },

    refresh() {
      const dt = this.getDataTableInstance();
      if (dt && this.useServerSide) {
        dt.ajax.reload();
      } else if (!this.useServerSide) {
        this.loading = true;
        this.$nextTick(() => {
          this.loading = false;
        });
      }
    },

    getSelectedRows() {
      const dt = this.getDataTableInstance();
      if (dt && this.selectable) {
        return dt.rows({ selected: true }).data().toArray();
      }
      return [];
    },

    clearSelection() {
      const dt = this.getDataTableInstance();
      if (dt && this.selectable) {
        dt.rows().deselect();
        this.internalSelectedRows = [];
        this.allSelected = false;
        this.$emit("update:selectedRows", []);
        this.$emit("selection-changed", []);
      }
    },

    selectRows(rowIds) {
      const dt = this.getDataTableInstance();
      if (dt && this.selectable) {
        dt.rows().deselect();
        dt.rows((idx, data) => rowIds.includes(data.id)).select();
      }
    },

    selectAllRows() {
      const dt = this.getDataTableInstance();
      if (dt && this.selectable) {
        if (this.rowSelectable) {
          // Only select rows that pass the rowSelectable filter
          dt.rows((idx, data) => this.rowSelectable(data)).select();
        } else {
          dt.rows().select();
        }
        this.allSelected = true;
      }
    },

    toggleSelectAll() {
      if (this.allSelected) {
        this.clearSelection();
      } else {
        this.selectAllRows();
      }
    },

    search(searchTerm) {
      const dt = this.getDataTableInstance();
      if (dt) {
        dt.search(searchTerm).draw();
      }
    },

    clearSearch() {
      const dt = this.getDataTableInstance();
      if (dt) {
        dt.search('').draw();
      }
    },
  },

  watch: {
    apiUrl: {
      handler(newUrl, oldUrl) {
        if (newUrl !== oldUrl && this.useServerSide) {
          this.$nextTick(() => {
            this.refresh();
          });
        }
      },
      immediate: false,
    },

    initialParams: {
      handler(newParams, oldParams) {
        if (this.isReloading) return;

        const newParamsStr = JSON.stringify(newParams);
        const oldParamsStr = JSON.stringify(oldParams);

        if (
          newParams &&
          oldParams &&
          newParamsStr !== oldParamsStr &&
          newParamsStr !== this.lastInitialParams
        ) {
          this.lastInitialParams = newParamsStr;
          this.isReloading = true;

          setTimeout(() => {
            this.refresh();
            setTimeout(() => {
              this.isReloading = false;
            }, 100);
          }, 100);
        }
      },
      deep: true,
      immediate: false,
    },

    tableData: {
      handler(newData) {
        if (!this.useServerSide && newData) {
          this.$nextTick(() => {
            this.refresh();
          });
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
/* Main Component Container */
.server-side-data-table {
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  background: var(--fundoForm);
  border-radius: 10px;
  color: var(--text);
  padding-top: 2rem;
  overflow: hidden;
}

.title_table {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 1rem;
}

.title_settings {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
}

.table_title {
  font-size: 1.2rem;
  font-weight: bold;
  color: var(--titulo);
}

.table_subtitle {
  margin: -0.75rem 0 0.75rem;
  font-size: 0.75rem;
  line-height: 1.3;
  color: var(--titulo);
  opacity: 0.65;
}

.action_buttons_div {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 1rem;
}

.left-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.right-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
}

.transfer-search-container {
  display: column;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 340px; 
  z-index: 10;
}

.transfer-multiselect {
  min-width: 260px;
  padding: 0.5rem;
}

.transfer-multiselect.disabled {
  opacity: 0.6;
  pointer-events: none;
  cursor: not-allowed;
}

/* Generic action button styling */
.action-btn {
  background: var(--fundoComponente);
  color: var(--text);
  padding: 0.3rem 0.6rem;
  border-radius: 0.375rem;
  border: 1px solid var(--glass-border);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background: var(--secundary);
  border-color: var(--glass-muted);
  transform: translateY(-1px);
}

.action-btn:active {
  transform: translateY(0);
}

/* Filter button */
.filter-btn.active {
  background: var(--primary);
  border-color: var(--button);
  color: var(--titulo);
  box-shadow: 0 0 0 2px var(--primary_1);
}

/* Export button (legacy support) */
.export-btn {
  background: var(--fundoComponente);
}

.export-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 0.5rem;
  background: var(--fundoComponente);
  border: 1px solid var(--glass-border);
  box-shadow: var(--glass-shadow);
  border-radius: 0.375rem;
  z-index: 10;
  width: 6.9rem;
}

.export-dropdown ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.export-dropdown li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  cursor: pointer;
  color: var(--text);
}

.export-dropdown li:hover {
  background: var(--primary);
  border-radius: 0.375rem;
}

/* Close button inherits from action-btn */
.close-btn {
  color: var(--icon);
}

.close-btn:hover {
  color: var(--obrigatorio);
}

/* Data Table Container */
.data-table-container {
  width: 100%;
  max-width: 100%;
  background: var(--fundoForm);
  border-radius: 0.5rem;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.data-table {
  width: 100%;
  min-width: 100%;
  color: var(--text);
  display: table;
}

.data-table.nowrap {
  white-space: nowrap;
}

/* Override nowrap for columns with max-width */
.data-table.nowrap :deep([class*="max-width-"]) {
  white-space: normal !important;
  word-wrap: break-word !important;
  word-break: break-word !important;
  overflow-wrap: break-word !important;
}

/* Table specific styles */
:deep(table) {
  background-color: transparent !important;
  width: 100%;
  color: var(--text);
  border-collapse: collapse;
  border-radius: 0.5rem;
  overflow: hidden;
}

:deep(thead th) {
  background-color: var(--colunaLista) !important;
  color: var(--label) !important;
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 600;
  border-bottom: 1px solid var(--glass-border);
  vertical-align: top;
  line-height: 1.4;
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
}

:deep(tbody td) {
  background-color: transparent !important;
  color: var(--text) !important;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--linhaLista);
  vertical-align: top;
  line-height: 1.4;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

:deep(tbody tr:hover td) {
  background-color: var(--primary_1) !important;
}

:deep(tbody tr.selected-row td) {
  background-color: var(--primary) !important;
}

/* Custom DataTables styling */
:deep(.dataTables_wrapper) {
  background: transparent !important;
  color: var(--text) !important;
  width: 100% !important;
  max-width: 100% !important;
  display: flex !important;
  flex-direction: column !important;
  height: 100% !important;
}

/* Fixed controls at top */
:deep(.dataTables_wrapper .dataTables_length),
:deep(.dataTables_wrapper .dataTables_filter) {
  flex-shrink: 0 !important;
  padding: 0.5rem 0 !important;
  background: var(--fundoForm) !important;
}

/* Scrollable table content */
:deep(.dataTables_wrapper .dataTables_scroll) {
  flex-grow: 1 !important;
  overflow: auto !important;
  max-height: 60vh !important;
}

:deep(.dataTables_wrapper .dataTables_scrollHead),
:deep(.dataTables_wrapper .dataTables_scrollBody) {
  width: 100% !important;
  overflow-x: auto !important;
}

:deep(.dataTables_wrapper .dataTables_scrollBody) {
  max-height: 60vh !important;
  overflow-y: auto !important;
}

/* Fixed controls at bottom */
:deep(.dataTables_wrapper .dataTables_info),
:deep(.dataTables_wrapper .dataTables_paginate) {
  flex-shrink: 0 !important;
  padding: 0.5rem 0 !important;
  background: var(--fundoForm) !important;
}

/* DataTables controls styling */
:deep(.dataTables_wrapper .dataTables_length),
:deep(.dataTables_wrapper .dataTables_filter),
:deep(.dataTables_wrapper .dataTables_info),
:deep(.dataTables_wrapper .dataTables_paginate) {
  background: var(--fundoForm) !important;
  color: var(--text) !important;
  padding: 0.75rem 1rem !important;
}

:deep(.dataTables_wrapper .dataTables_length select),
:deep(.dataTables_wrapper .dataTables_filter input) {
  background: var(--fundoComponente) !important;
  color: var(--text) !important;
  border: 1px solid var(--glass-border) !important;
  border-radius: 0.375rem !important;
  padding: 0.25rem 0.5rem !important;
}

:deep(.dataTables_length),
:deep(.dataTables_filter),
:deep(.dataTables_info),
:deep(.dataTables_paginate) {
  display: none !important; /* Hide default controls since we have custom ones */
}

:deep(.dataTables_processing) {
  background-color: var(--fundoForm) !important;
  color: var(--text) !important;
  border: 1px solid var(--glass-border) !important;
}

:deep(.dataTables_empty) {
  color: var(--glass-muted) !important;
}

/* Hide DataTables controls when using custom ones */
:deep(.dataTables_length),
:deep(.dataTables_filter) {
  display: none !important;
}

/* Show DataTables controls when using serverSide */
.server-side :deep(.dataTables_length),
.server-side :deep(.dataTables_filter) {
  display: block !important;
  color: var(--text) !important;
}

.server-side :deep(.dataTables_length select),
.server-side :deep(.dataTables_filter input) {
  background-color: var(--fundoForm) !important;
  color: var(--text) !important;
  border: 1px solid var(--glass-border) !important;
  border-radius: 0.375rem !important;
  padding: 0.25rem !important;
}

/* Custom Pagination Styling */
.custom-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background: var(--fundoForm);
  border-radius: 0.375rem;
  color: var(--text);
}

.pagination-info {
  font-size: 0.875rem;
  color: var(--glass-muted);
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.page-size-select {
  background: var(--fundoComponente);
  color: var(--text);
  border: 1px solid var(--glass-border);
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
}

.pagination-btn {
  background: var(--fundoComponente);
  color: var(--text);
  border: 1px solid var(--glass-border);
  border-radius: 0.375rem;
  padding: 0.5rem;
  cursor: pointer;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn:hover:not(:disabled) {
  background: var(--secundary);
}

.page-info {
  font-size: 0.875rem;
  color: var(--text);
}

/* Loading and Empty States */
.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: var(--text);
}

.loading-spinner {
  border: 3px solid var(--glass-border);
  border-radius: 50%;
  border-top: 3px solid var(--text);
  width: 2rem;
  height: 2rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: var(--glass-muted);
  font-style: italic;
}

/* Max width column classes */
:deep(.max-width-100),
:deep(.max-width-100px) {
  max-width: 100px !important;
  min-width: 100px !important;
  width: 100px !important;
}
:deep(.max-width-150),
:deep(.max-width-150px) {
  max-width: 150px !important;
  min-width: 150px !important;
  width: 150px !important;
}
:deep(.max-width-200),
:deep(.max-width-200px) {
  max-width: 200px !important;
  min-width: 200px !important;
  width: 200px !important;
}
:deep(.max-width-250),
:deep(.max-width-250px) {
  max-width: 250px !important;
  min-width: 250px !important;
  width: 250px !important;
}
:deep(.max-width-300),
:deep(.max-width-300px) {
  max-width: 300px !important;
  min-width: 300px !important;
  width: 300px !important;
}
:deep(.max-width-400),
:deep(.max-width-400px) {
  max-width: 400px !important;
  min-width: 400px !important;
  width: 400px !important;
}
:deep(.max-width-500),
:deep(.max-width-500px) {
  max-width: 500px !important;
  min-width: 500px !important;
  width: 500px !important;
}

/* Generic max-width classes for any pixel value */
:deep([class*="max-width-"]) {
  word-wrap: break-word !important;
  word-break: break-word !important;
  white-space: normal !important;
  overflow-wrap: break-word !important;
}

/* Apply max-width to both th and td elements */
:deep(thead th[class*="max-width-"]),
:deep(tbody td[class*="max-width-"]) {
  word-wrap: break-word !important;
  word-break: break-word !important;
  white-space: normal !important;
  overflow-wrap: break-word !important;
  vertical-align: top;
}

/* Force DataTables to respect our width settings */
:deep(.dataTables_wrapper table.dataTable thead th[class*="max-width-"]),
:deep(.dataTables_wrapper table.dataTable tbody td[class*="max-width-"]) {
  max-width: inherit !important;
  min-width: inherit !important;
  width: inherit !important;
  box-sizing: border-box !important;
}

/* Override DataTables auto-width calculations */
:deep(.dataTables_wrapper table.dataTable) {
  table-layout: fixed !important;
  width: 100% !important;
}

/* Scrollbar styling */
.scrollbar-thin {
  scrollbar-width: thin;
}

.scrollbar-tema {
  scrollbar-width: thin;
  scrollbar-color: var(--scroll) transparent;
}

/* Drag and Drop styles */
.cursor-move {
  cursor: move;
}

.cursor-move:hover {
  background-color: rgba(59, 130, 246, 0.1);
}

.cursor-move:active {
  opacity: 0.5;
}

/* Drag over effect */
tr.drag-over {
  background-color: rgba(59, 130, 246, 0.2);
  border-top: 2px solid #3b82f6;
}

/* Editable cell styling */
td input[type="number"],
td input[type="text"] {
  background-color: var(--fundoComponente);
  color: var(--text);
  border-color: var(--glass-border);
}

td input[type="number"]:focus,
td input[type="text"]:focus {
  background-color: var(--secundary);
  border-color: var(--button);
}

/* Row selection styles */
:deep(tbody tr.selected) {
  background-color: rgba(59, 130, 246, 0.25) !important;
}

:deep(tbody tr.selected:hover) {
  background-color: rgba(59, 130, 246, 0.35) !important;
}

:deep(tbody tr.selected td) {
  background-color: rgba(59, 130, 246, 0.25) !important;
}

:deep(.select-checkbox) {
  text-align: center !important;
  cursor: pointer !important;
  padding: 0.75rem !important;
}

:deep(.select-checkbox input[type="checkbox"]) {
  cursor: pointer;
  width: 16px;
  height: 16px;
  accent-color: #3b82f6;
}

:deep(.dt-select-checkbox) {
  position: relative;
}

:deep(.dt-select-checkbox::before) {
  display: none !important;
}

/* Selection info styling */
:deep(.dataTables_info) {
  color: var(--text) !important;
}

/* Boolean Toggle Switch Styling */
.boolean-toggle-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
}

/* The switch - the box around the slider */
:deep(.switch) {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
}

/* Hide default HTML checkbox */
:deep(.switch input) {
  opacity: 0;
  width: 0;
  height: 0;
}

/* The slider */
:deep(.slider) {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

:deep(.slider:before) {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

:deep(input:checked + .slider) {
  background-color: #2196f3;
}

:deep(input:focus + .slider) {
  box-shadow: 0 0 1px #2196f3;
}

:deep(input:checked + .slider:before) {
  -webkit-transform: translateX(18px);
  -ms-transform: translateX(18px);
  transform: translateX(18px);
}

/* Rounded sliders */
:deep(.slider.round) {
  border-radius: 34px;
}

:deep(.slider.round:before) {
  border-radius: 50%;
}

.boolean-value-text {
  font-size: 0.875rem;
  color: var(--text);
  font-weight: 500;
  min-width: 30px;
}

/* Ensure boolean toggle doesn't interfere with row selection */
:deep(.boolean-toggle-wrapper) {
  pointer-events: auto;
}

:deep(.boolean-toggle-wrapper *) {
  pointer-events: auto;
}

/* Number Input Styling */
:deep(.number-input-wrapper) {
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
}

:deep(.number-input-field) {
  width: 80px;
  padding: 4px 8px;
  border: 1px solid var(--glass-border);
  border-radius: 4px;
  font-size: 14px;
  text-align: center;
  background-color: var(--fundoComponente);
  color: var(--text);
  transition: all 0.2s ease;
}

:deep(.number-input-field:focus) {
  outline: none;
  border-color: var(--button);
  background-color: var(--secundary);
  box-shadow: 0 0 0 2px var(--primary_1);
}

:deep(.number-input-field:hover) {
  border-color: var(--glass-muted);
  background-color: var(--colunaLista);
}

/* Remove spinner arrows for number input (optional) */
:deep(.number-input-field::-webkit-outer-spin-button),
:deep(.number-input-field::-webkit-inner-spin-button) {
  -webkit-appearance: none;
  margin: 0;
}

:deep(.number-input-field[type="number"]) {
  -moz-appearance: textfield;
}

:deep(.number-input-wrapper) {
  pointer-events: auto;
}

:deep(.number-input-wrapper *) {
  pointer-events: auto;
}

/* Actions Cell Wrapper - Handles multiple action buttons */
:deep(.actions-cell-wrapper) {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  flex-wrap: wrap;
  padding: 0.125rem 0;
  min-height: 28px;
}

/* Action Cell Button - Improved styling for multiple actions */
:deep(.action-cell-btn) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.25rem 0.375rem;
  border-radius: 0.25rem;
  border: none;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  line-height: 1;
  min-height: 24px;
}

:deep(.action-cell-btn:hover) {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

:deep(.action-cell-btn:active) {
  transform: translateY(0);
}

/* Action button icon */
:deep(.action-cell-btn i) {
  font-size: 0.875rem;
  line-height: 1;
}

/* Action button text */
:deep(.action-cell-btn .action-text) {
  font-size: 0.75rem;
  line-height: 1;
  margin-left: 0.25rem;
}

/* Specific button type overrides */
:deep(.action-cell-btn.bg-blue-500:hover) {
  background-color: #2563eb !important;
}

:deep(.action-cell-btn.bg-red-500:hover) {
  background-color: #dc2626 !important;
}

:deep(.action-cell-btn.bg-green-500:hover) {
  background-color: #16a34a !important;
}

:deep(.action-cell-btn.bg-purple-600:hover) {
  background-color: #7c3aed !important;
}

:deep(.action-cell-btn.bg-yellow-600:hover) {
  background-color: #ca8a04 !important;
}

/* Responsive handling for action columns */
@media (max-width: 768px) {
  :deep(.actions-cell-wrapper) {
    gap: 0.25rem;
  }

  :deep(.action-cell-btn) {
    padding: 0.25rem 0.375rem;
    font-size: 0.7rem;
  }

  :deep(.action-cell-btn .action-text) {
    display: none;
  }
}
</style>

<style>
@import "~datatables.net-dt/css/dataTables.dataTables.min.css";
@import "~datatables.net-responsive-dt/css/responsive.dataTables.min.css";
@import "~datatables.net-buttons-dt/css/buttons.dataTables.min.css";
@import "~datatables.net-select-dt/css/select.dataTables.min.css";
</style>
